import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import notify, { currentLanguage } from "../../common/ui/notify";
import { LoadingBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { formatPrice, formatUsdFromGel, carTitle, toImageArray, formatMileage, formatEngine, display, isBlank, translateSpec, localizeEventTitle, flagEmoji, countryName } from "../../common/format";
import { getSocket, emitAck } from "../../common/auctionSocket";
import useWatchlist from "../../common/useWatchlist";
import WatchButton from "./WatchButton";
import CarStatusBadges from "../../common/CarStatusBadges";
import { playBid, playOutbid, unlockAudio } from "../../common/auctionSounds";
import { t } from "./auctionI18n";
import { StarFill, ChevronLeft, ChevronRight, TrophyFill, ExclamationTriangleFill, VolumeUpFill, VolumeMuteFill, GeoAltFill, PersonPlusFill } from "react-bootstrap-icons";
import "./Auction.css";
import "../../common/ui/ui.css";

const REASON = {
    too_low: { geo: "ბიდი მინიმუმზე დაბალია.", en: "Your bid is below the minimum.", ru: "Ставка ниже минимума.", am: "Հայտը նվազագույնից ցածր է։" },
    insufficient_deposit: { geo: "დეპოზიტი არ არის საკმარისი (ბიდის 10%).", en: "Not enough free deposit (10% of the bid).", ru: "Недостаточно свободного депозита (10% от ставки).", am: "Ազատ ավանդը բավարար չէ (հայտի 10%)։" },
    phone_not_verified: { geo: "ჯერ დაადასტურეთ ტელეფონი.", en: "Verify your phone first.", ru: "Сначала подтвердите телефон.", am: "Նախ հաստատեք հեռախոսը։" },
    kyc_not_approved: { geo: "ჯერ უნდა დადასტურდეს KYC.", en: "KYC must be approved.", ru: "KYC должен быть одобрен.", am: "KYC-ը պետք է հաստատված լինի։" },
    not_authenticated: { geo: "გთხოვთ შეხვიდეთ სისტემაში.", en: "Please sign in.", ru: "Пожалуйста, войдите.", am: "Խնդրում ենք մուտք գործել։" },
    lot_not_open: { geo: "ბიდისთვის დახურულია.", en: "Not open for bidding.", ru: "Ставки закрыты.", am: "Հայտերի համար փակ է։" },
    lot_ended: { geo: "ლოტი დასრულდა.", en: "This lot has ended.", ru: "Лот завершён.", am: "Լոտն ավարտվել է։" },
    event_paused: { geo: "აუქციონი შეჩერებულია.", en: "The auction is paused.", ru: "Аукцион приостановлен.", am: "Աճուրդը դադարեցված է։" },
    not_higher_than_your_max: { geo: "შეიყვანეთ თქვენს მაქსიმუმზე მეტი.", en: "Enter more than your max.", ru: "Введите больше вашего максимума.", am: "Մուտքագրեք ձեր առավելագույնից ավելին։" },
    lot_busy: { geo: "დატვირთულია, სცადეთ თავიდან.", en: "Too busy, try again.", ru: "Занято, попробуйте снова.", am: "Զբաղված է, փորձեք նորից։" },
};

function fmtRemaining(ms) {
    if (ms == null) return "—";
    if (ms <= 0) return "00:00";
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// Longer countdown to the event start (supports hours).
function fmtStart(ms) {
    if (ms == null || ms <= 0) return "00:00";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

const DONE = ["sold", "closed", "withdrawn"];

// Physical auction venue, shown on the waiting-room screen. The location name is
// run through translateSpec so it follows the site language; the map is a fixed
// Google Maps embed of the Rustavi Auto Market.
const VENUE_GEO = "რუსთავის ავტო ბაზრობა";
const VENUE_MAP = "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d873.2439039643219!2d44.95470522529834!3d41.565313101982376!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2sge!4v1793712763066!5m2!1sen!2sge";

const AuctionLive = () => {
    const { id } = useParams();
    const eventId = parseInt(id, 10);

    const [event, setEvent] = useState(null);
    const [queue, setQueue] = useState([]);
    const [currentLotId, setCurrentLotId] = useState(null);
    const [currentLot, setCurrentLot] = useState(null);     // full car data
    const [live, setLive] = useState(null);                 // socket state
    const [me, setMe] = useState(null);
    const [loadState, setLoadState] = useState("loading");
    const [now, setNow] = useState(Date.now());
    const [maxInput, setMaxInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [finished, setFinished] = useState(false);
    const [intermission, setIntermission] = useState(null); // { until, result }
    const [imgIdx, setImgIdx] = useState(0);                // current gallery photo
    const [myActive, setMyActive] = useState(false);        // have I bid on the current lot?
    const [feed, setFeed] = useState([]);                   // live bid feed (name + country)
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem("auction_sound") === "off"; } catch (e) { return false; }
    });
    const mutedRef = useRef(muted);
    mutedRef.current = muted;
    const toggleMuted = () => setMuted((m) => {
        const next = !m;
        try { localStorage.setItem("auction_sound", next ? "off" : "on"); } catch (e) { /* ignore */ }
        if (!next) unlockAudio();
        return next;
    });

    const currentRef = useRef(null);
    currentRef.current = currentLotId;
    const prevImageRef = useRef(null);
    const currentRowRef = useRef(null); // the live lot's row in the side queue
    const watch = useWatchlist();

    const refreshQueue = useCallback(() => {
        return axios.get(`/api/auction/events/${eventId}`)
            .then((res) => { setEvent(res.data.event); setQueue(res.data.lots || []); return res.data; })
            .catch(() => null);
    }, [eventId]);

    // Initial load + identity.
    useEffect(() => {
        let alive = true;
        Promise.all([
            axios.get(`/api/auction/events/${eventId}`),
            axios.get("/api/auction/me").catch(() => null),
        ]).then(([ev, meRes]) => {
            if (!alive) return;
            setEvent(ev.data.event);
            setQueue(ev.data.lots || []);
            if (meRes) setMe({ id: meRes.data.user.id, eligibility: meRes.data.eligibility });
            if (ev.data.event && ev.data.event.status === "finished") setFinished(true);
            setLoadState("ready");
        }).catch(() => { if (alive) setLoadState("error"); });
        return () => { alive = false; };
    }, [eventId]);

    // Socket: join the event room, react to auto-advance.
    useEffect(() => {
        if (!Number.isInteger(eventId)) return undefined;
        const socket = getSocket();

        const onState = (s) => { if (s && s.lot_id === currentRef.current) setLive(s); };
        const onNext = (m) => {
            if (!m || m.event_id !== eventId) return;
            setFinished(false);
            setIntermission(null);
            setCurrentLotId(m.lot_id);
            refreshQueue();
        };
        const onIntermission = (m) => {
            if (!m || m.event_id !== eventId) return;
            setIntermission({ until: Date.now() + (Number(m.seconds) || 5) * 1000, result: m.result || null });
            refreshQueue();
        };
        const onClosed = (m) => {
            if (!m || !m.result) return;
            if (m.result.lot_id === currentRef.current) {
                setLive((p) => (p ? { ...p, status: m.result.status || "closed" } : p));
            }
            refreshQueue();
        };
        const onFinished = (m) => { if (m && m.event_id === eventId) { setFinished(true); setCurrentLotId(null); setLive(null); refreshQueue(); } };
        const onOutbid = (m) => { if (m && m.lot_id === currentRef.current) { setMyActive(true); if (!mutedRef.current) playOutbid(); notify.warning({ geo: "თქვენ გადაგაჭარბეს!", en: "You've been outbid!", ru: "Вашу ставку перебили!", am: "Ձեր հայտը գերազանցվեց!" }); } };
        const onExtended = (m) => { if (m && m.lot_id === currentRef.current) { setLive((p) => (p ? { ...p, ends_at: m.ends_at } : p)); notify.info({ geo: "დრო გაგრძელდა!", en: "Time extended!", ru: "Время продлено!", am: "Ժամանակը երկարաձգվեց!" }); } };
        const onOutcome = (m) => { if (m && m.decision === "accepted") notify.success({ geo: "თქვენ მოიგეთ ეს ლოტი!", en: "You won this lot!", ru: "Вы выиграли этот лот!", am: "Դուք շահեցիք այս լոտը!" }); };
        const onFeed = (m) => {
            if (!m || m.lot_id !== currentRef.current) return;
            setFeed((prev) => [{ ...m, key: `${m.lot_id}-${m.ts || Date.now()}-${prev.length}` }, ...prev].slice(0, 12));
        };

        socket.on("lot:state", onState);
        socket.on("event:next_lot", onNext);
        socket.on("event:intermission", onIntermission);
        socket.on("lot:closed", onClosed);
        socket.on("event:finished", onFinished);
        socket.on("outbid", onOutbid);
        socket.on("lot:extended", onExtended);
        socket.on("lot:outcome", onOutcome);
        socket.on("bid:feed", onFeed);

        emitAck("event:join", { event_id: eventId }).then((ack) => {
            if (ack && ack.ok) {
                if (ack.event_status === "finished") setFinished(true);
                if (ack.current_lot_id) { setCurrentLotId(ack.current_lot_id); if (ack.state) setLive(ack.state); }
            }
        });

        return () => {
            socket.off("lot:state", onState);
            socket.off("event:next_lot", onNext);
            socket.off("event:intermission", onIntermission);
            socket.off("lot:closed", onClosed);
            socket.off("event:finished", onFinished);
            socket.off("outbid", onOutbid);
            socket.off("lot:extended", onExtended);
            socket.off("lot:outcome", onOutcome);
            socket.off("bid:feed", onFeed);
        };
    }, [eventId, refreshQueue]);

    // When the current lot changes: load its car data and join its room.
    useEffect(() => {
        if (!currentLotId) { setCurrentLot(null); return undefined; }
        let alive = true;
        setImgIdx(0); // restart the gallery on a new lot
        setMyActive(false); // reset my participation flag for the new lot
        setFeed([]); // each lot shows its own live bid feed
        axios.get(`/api/auction/lots/${currentLotId}`)
            .then((res) => { if (alive) setCurrentLot(res.data.lot); })
            .catch(() => {});
        emitAck("lot:join", { lot_id: currentLotId }).then((ack) => { if (alive && ack && ack.state) setLive(ack.state); });
        return () => { alive = false; };
    }, [currentLotId]);

    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);

    // Keep the side queue scrolled to whichever lot is live right now, so the car
    // currently on the block is always visible as the auction advances.
    useEffect(() => {
        if (!currentLotId || !currentRowRef.current) return;
        currentRowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [currentLotId]);

    // Auto-rotate the current lot's photos every 5s. A manual switch changes
    // imgIdx, which restarts the 5s timer (imgIdx is a dependency).
    const galleryLen = toImageArray(currentLot && currentLot.images).length;
    useEffect(() => {
        if (galleryLen <= 1) return undefined;
        const t = setInterval(() => setImgIdx((i) => (i + 1) % galleryLen), 5000);
        return () => clearInterval(t);
    }, [galleryLen, currentLotId, imgIdx]);

    const place = useCallback(async (ev, amount) => {
        if (busy || !currentLotId) return;
        if (!me) { notify.warning(REASON.not_authenticated); return; }
        unlockAudio();
        setBusy(true);
        try {
            const ack = await emitAck(ev, ev === "proxy:set" ? { lot_id: currentLotId, max: amount } : { lot_id: currentLotId, amount });
            if (ack && ack.ok) {
                setMyActive(true);
                if (!mutedRef.current) playBid();
                notify.success(ack.leading ? { geo: "თქვენ ლიდერობთ!", en: "You're leading!", ru: "Вы лидируете!", am: "Դուք առաջատար եք!" } : { geo: "ბიდი დადებულია.", en: "Bid placed.", ru: "Ставка сделана.", am: "Հայտը դրվեց։" });
                if (ack.state) setLive(ack.state);
            } else {
                notify.error(REASON[ack && ack.reason] || { geo: "ბიდი უარყოფილია.", en: "Bid rejected.", ru: "Ставка отклонена.", am: "Հայտը մերժվեց։" });
            }
        } finally { setBusy(false); }
    }, [busy, me, currentLotId]);

    if (loadState === "loading") return <div className="container auction-live"><LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում...")} /></div>;
    if (loadState === "error" || !event) return <div className="container auction-live"><ErrorBlock title={t("ვერ მოიძებნა", "Not found", "Не найдено", "Չի գտնվել")} message={t("აუქციონი ვერ მოიძებნა.", "Auction not found.", "Аукцион не найден.", "Աճուրդը չի գտնվել։")} /></div>;

    const sortedQueue = [...queue].sort((a, b) => (a.position - b.position) || (a.id - b.id));
    const totalLots = sortedQueue.length;
    const currentIndex = sortedQueue.findIndex((l) => l.id === currentLotId);
    const doneCount = sortedQueue.filter((l) => DONE.includes(l.status)).length;
    const reached = currentIndex >= 0 ? currentIndex + 1 : doneCount;
    const progressPct = totalLots ? Math.round((reached / totalLots) * 100) : 0;
    const price = live ? live.current_price : (currentLot ? (currentLot.current_price || currentLot.start_price) : null);
    const usdRate = event && event.usd_rate ? Number(event.usd_rate) : null;
    const status = live ? live.status : (currentLot ? currentLot.status : null);
    const minNext = live && live.min_next_bid != null ? live.min_next_bid : (currentLot ? Number(currentLot.start_price) : 0);
    const isLive = status === "live";
    const endsAt = live && live.ends_at ? new Date(live.ends_at).getTime() : null;
    const remaining = endsAt ? endsAt - now : null;
    const leadingMe = me && live && live.leading === me.id;
    const images = toImageArray(currentLot && currentLot.images);
    const safeImgIdx = images.length ? imgIdx % images.length : 0;
    const activeImage = images[safeImgIdx] || images[0] || null;
    // Previous photo stays underneath so the new one can cross-fade over it.
    const prevImage = prevImageRef.current;
    prevImageRef.current = activeImage;
    const canBid = me && me.eligibility && me.eligibility.eligible;
    const urgent = isLive && remaining != null && remaining <= 10000;

    // The signed-in bidder's own live status, always shown while a lot is on
    // stage: winning (leader), outbid (bid but overtaken), or not-yet-bidding.
    const lotActive = status === "live" || status === "open";
    const myStatus = me && currentLotId && lotActive
        ? (leadingMe ? "winning" : (myActive ? "outbid" : "idle"))
        : null;

    // Circular countdown ring: fills 0 -> 100% as the window elapses.
    const windowMs = (Number(event.lot_duration_seconds) > 0 ? Number(event.lot_duration_seconds) : 15) * 1000;
    const elapsedFrac = isLive && remaining != null ? Math.min(1, Math.max(0, 1 - remaining / windowMs)) : 0;
    const RING_R = 54;
    const RING_C = 2 * Math.PI * RING_R;

    // Countdown to the event start (while scheduled).
    const startsInMs = event.status === "scheduled" && event.starts_at ? new Date(event.starts_at).getTime() - now : 0;

    const interSeconds = intermission ? Math.max(0, Math.ceil((intermission.until - now) / 1000)) : 0;
    const soldInfo = (result) => {
        const done = { label: t("ლოტი დასრულდა", "Lot ended", "Лот завершён", "Լոտն ավարտվեց"), cls: "neutral" };
        if (!result) return done;
        if (result.status === "withdrawn") return { label: t("მოხსნილია", "Withdrawn", "Снят", "Հանված"), cls: "neutral" };
        if (result.decision === "accepted" || result.status === "sold") return { label: t("გაიყიდა!", "Sold!", "Продано!", "Վաճառված է!"), cls: "sold", price: result.winning_bid };
        if (result.decision === "pending_seller") return { label: t("reserve-ს ქვემოთ", "Below reserve", "Ниже резерва", "Ռեզերվից ցածր"), sub: t("ოპერატორის განხილვაში", "Under operator review", "На рассмотрении оператора", "Օպերատորի քննարկման մեջ"), cls: "pending", price: result.winning_bid };
        if (result.decision === "unsold" || !result.winner_id) return { label: t("არ გაიყიდა", "Not sold", "Не продано", "Չի վաճառվել"), cls: "neutral" };
        return done;
    };

    const lang = currentLanguage();
    const lotSpecs = currentLot ? [
        { label: t("წელი", "Year", "Год", "Տարի"), value: currentLot.year },
        { label: "VIN", value: currentLot.vin },
        { label: t("გარბენი", "Mileage", "Пробег", "Վազք"), value: isBlank(currentLot.millage) ? null : formatMileage(currentLot.millage) },
        { label: t("საწვავი", "Fuel", "Топливо", "Վառելիք"), value: translateSpec("fuel", currentLot.fuel, lang) },
        { label: t("ძრავი", "Engine", "Двигатель", "Շարժիչ"), value: isBlank(currentLot.engine) ? null : formatEngine(currentLot.engine) },
        { label: t("ცილინდრები", "Cylinders", "Цилиндры", "Մխոցներ"), value: currentLot.cilinder },
        { label: t("გადაცემათა კოლოფი", "Transmission", "Коробка передач", "Փոխանցումատուփ"), value: translateSpec("transmission", currentLot.transmission, lang) },
        { label: t("წამყვანი თვლები", "Drive", "Привод", "Քարշակ"), value: translateSpec("drive", currentLot.drive, lang) },
        { label: t("კარები", "Doors", "Двери", "Դռներ"), value: currentLot.doors },
        { label: t("უსაფრთხოების ბალიშები", "Airbags", "Подушки безопасности", "Անվտանգության բարձիկներ"), value: currentLot.airbags },
        { label: t("ფერი", "Color", "Цвет", "Գույն"), value: translateSpec("color", currentLot.color, lang) },
        { label: t("კატეგორია", "Category", "Категория", "Կատեգորիա"), value: translateSpec("category", currentLot.category, lang) },
        { label: t("ლოკაცია", "Location", "Локация", "Տեղ"), value: translateSpec("location", currentLot.location, lang) },
    ].filter((r) => !isBlank(r.value)) : [];

    const chipFor = (lot) => {
        if (lot.status === "live") return <span className="auction-chip auction-chip-live">{t("ლაივ", "Live", "В эфире", "Ուղիղ")}</span>;
        if (lot.status === "sold") return <span className="auction-chip auction-chip-sold">{t("გაყიდული", "Sold", "Продано", "Վաճառված")}</span>;
        if (DONE.includes(lot.status)) return <span className="auction-chip">{t("დასრულდა", "Ended", "Завершено", "Ավարտ")}</span>;
        return <span className="auction-chip">{t("რიგში", "Queued", "В очереди", "Հերթում")}</span>;
    };

    return (
        <div className="container auction-live">
            <Link to={`/auction/event/${eventId}`} className="auction-back auction-live-back">← {t("აუქციონი", "Auction", "Аукцион", "Աճուրդ")}</Link>
            <div className="auction-live-topbar cx-card">
                <div className="auction-live-title">
                    {event.status === "live"
                        ? <span className="auction-live-badge"><span className="auction-live-dot" /> LIVE</span>
                        : <span className={`auction-chip auction-chip-${event.status}`}>{event.status}</span>}
                    <h1>{localizeEventTitle(event.title, lang) || `${t("აუქციონი", "Auction", "Аукцион", "Աճուրդ")} #${event.id}`}</h1>
                </div>
                {totalLots > 0 && (
                    <div className="auction-live-progress">
                        <div className="auction-live-progress-meta">
                            <span className="auction-live-counter">{t("ლოტი", "Lot", "Лот", "Լոտ")} {currentIndex >= 0 ? currentIndex + 1 : doneCount} / {totalLots}</span>
                            <span className="auction-live-registered-count">{totalLots} {t("დარეგისტრირებული", "registered", "зарегистрировано", "գրանցված")}</span>
                        </div>
                        <div className="auction-live-progress-bar"><span style={{ width: `${progressPct}%` }} /></div>
                    </div>
                )}
                <button type="button" className={`auction-sound-toggle ${muted ? "is-muted" : ""}`} onClick={toggleMuted}
                    title={muted ? t("ხმის ჩართვა", "Turn sound on", "Включить звук", "Միացնել ձայնը") : t("ხმის გამორთვა", "Turn sound off", "Выключить звук", "Անջատել ձայնը")} aria-label={muted ? t("ხმის ჩართვა", "Turn sound on", "Включить звук", "Միացնել ձայնը") : t("ხმის გამორთვა", "Turn sound off", "Выключить звук", "Անջատել ձայնը")}>
                    {muted ? <VolumeMuteFill /> : <VolumeUpFill />}
                </button>
            </div>

            <div className="auction-live-grid">
                {/* Current lot stage */}
                <div className="auction-live-stage">
                    {finished ? (
                        <div className="cx-card auction-live-empty">
                            <h2>{t("აუქციონი დასრულდა", "The auction has ended", "Аукцион завершён", "Աճուրդն ավարտվեց")}</h2>
                            <p className="auction-muted">{t("მადლობა მონაწილეობისთვის.", "Thank you for taking part.", "Спасибо за участие.", "Շնորհակալություն մասնակցության համար։")}</p>
                        </div>
                    ) : intermission ? (
                        (() => {
                            const info = soldInfo(intermission.result);
                            return (
                                <div className={`cx-card auction-live-empty auction-sold ${info.cls}`}>
                                    <div className="auction-sold-stamp">{info.label}</div>
                                    {info.price != null && <div className="auction-sold-price">{formatPrice(info.price, "GEL")}{usdRate && formatUsdFromGel(info.price, usdRate) ? <span className="auction-usd">≈ {formatUsdFromGel(info.price, usdRate)}</span> : null}</div>}
                                    {info.sub && <div className="auction-muted auction-sold-sub">{info.sub}</div>}
                                    <div className="auction-sold-next">{t("შემდეგი ლოტი:", "Next lot:", "Следующий лот:", "Հաջորդ լոտ.")} {interSeconds} {t("წმ", "s", "с", "վրկ")}</div>
                                </div>
                            );
                        })()
                    ) : !currentLotId ? (
                        <>
                        <div className="cx-card auction-live-empty">
                            {startsInMs > 0 ? (
                                <>
                                    <h2>{t("აუქციონი დაიწყება", "The auction starts in", "Аукцион начнётся через", "Աճուրդը կսկսվի")}</h2>
                                    <div className="auction-start-countdown">{fmtStart(startsInMs)}</div>
                                    <p className="auction-muted">{t("დაწყებისთანავე პირველი ლოტი ავტომატურად გამოჩნდება.", "The first lot will appear automatically when it starts.", "Первый лот появится автоматически после начала.", "Առաջին լոտը ավտոմատ կհայտնվի մեկնարկից հետո։")}</p>
                                </>
                            ) : (
                                <>
                                    <h2>{t("აუქციონი იწყება...", "The auction is starting...", "Аукцион начинается...", "Աճուրդը սկսվում է...")}</h2>
                                    <p className="auction-muted">{t("პირველი ლოტი ავტომატურად გამოჩნდება აქ.", "The first lot will appear here automatically.", "Первый лот появится здесь автоматически.", "Առաջին լոտն ավտոմատ կհայտնվի այստեղ։")}</p>
                                </>
                            )}
                        </div>
                        <div className="cx-card auction-venue">
                            <div className="auction-venue-head">
                                <GeoAltFill aria-hidden="true" />
                                <span>{translateSpec("location", VENUE_GEO, lang)}</span>
                            </div>
                            <div className="auction-venue-map">
                                <iframe
                                    src={VENUE_MAP}
                                    title={t("აუქციონის მდებარეობა", "Auction location", "Местоположение аукциона", "Աճուրդի վայրը")}
                                    loading="lazy"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                            </div>
                            {!me && (
                                <Link to="/auction/register" className="cx-btn cx-btn-primary auction-venue-cta">
                                    <PersonPlusFill aria-hidden="true" /> {t("რეგისტრაცია აუქციონზე", "Register for the auction", "Регистрация на аукцион", "Գրանցվել աճուրդին")}
                                </Link>
                            )}
                        </div>
                        </>
                    ) : (
                        <>
                            <div className="auction-live-left">
                            <div className="auction-live-media">
                                <div className="auction-room-image auction-live-stageimg">
                                    <div className="auction-live-photo" style={(prevImage || activeImage) ? { backgroundImage: `url('${prevImage || activeImage}')` } : {}} />
                                    <div className="auction-live-photo auction-live-photo-top" key={`${currentLotId}-${safeImgIdx}`} style={activeImage ? { backgroundImage: `url('${activeImage}')` } : {}} />
                                    {currentIndex >= 0 && <span className="auction-live-lotno">{t("ლოტი", "Lot", "Лот", "Լոտ")} {currentIndex + 1}</span>}
                                    {isLive && <span className="auction-live-onair"><span className="auction-live-dot" /> {t("ლაივში", "Live", "В эфире", "Ուղիղ")}</span>}
                                    {images.length > 1 && (
                                        <>
                                            <button type="button" className="auction-live-navbtn is-prev" aria-label={t("წინა ფოტო", "Previous photo", "Предыдущее фото", "Նախորդ լուսանկար")}
                                                onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}><ChevronLeft /></button>
                                            <button type="button" className="auction-live-navbtn is-next" aria-label={t("შემდეგი ფოტო", "Next photo", "Следующее фото", "Հաջորդ լուսանկար")}
                                                onClick={() => setImgIdx((i) => (i + 1) % images.length)}><ChevronRight /></button>
                                            <div className="auction-live-dots">
                                                {images.map((_, i) => (
                                                    <button key={i} type="button" aria-label={`${t("ფოტო", "Photo", "Фото", "Լուսանկար")} ${i + 1}`}
                                                        className={`auction-live-dot-btn ${i === safeImgIdx ? "is-on" : ""}`}
                                                        onClick={() => setImgIdx(i)} />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {images.length > 1 && (
                                    <div className="auction-live-thumbs">
                                        {images.slice(0, 8).map((img, i) => (
                                            <button key={i} type="button" aria-label={`${t("ფოტო", "Photo", "Фото", "Լուսանկար")} ${i + 1}`}
                                                className={`auction-live-thumb ${i === safeImgIdx ? "is-active" : ""}`}
                                                style={{ backgroundImage: `url('${img}')` }} onClick={() => setImgIdx(i)} />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Detailed car data, right under the photo carousel */}
                            {currentLot && (lotSpecs.length > 0 || !isBlank(currentLot.description)) && (
                                <div className="cx-card auction-live-specsbox">
                                    <h3 className="auction-live-specs-title">{t("დეტალური ინფორმაცია", "Detailed information", "Подробная информация", "Մանրամասն տեղեկություն")}</h3>
                                    {lotSpecs.length > 0 && (
                                        <dl className="auction-specs">
                                            {lotSpecs.map((r) => (
                                                <div className="auction-spec-row" key={r.label}><dt>{r.label}</dt><dd>{display(r.value)}</dd></div>
                                            ))}
                                        </dl>
                                    )}
                                    {!isBlank(currentLot.description) && <p className="auction-desc auction-live-specs-desc">{display(currentLot.description)}</p>}
                                </div>
                            )}
                            </div>
                            <div className="cx-card auction-room-panel auction-live-panel">
                                {myStatus && (
                                    <div className={`auction-mystatus is-${myStatus}`}>
                                        {myStatus === "winning" && <><TrophyFill /> <span>{t("თქვენ იგებთ", "You're winning", "Вы выигрываете", "Դուք հաղթում եք")}</span></>}
                                        {myStatus === "outbid" && <><ExclamationTriangleFill /> <span>{t("აუთბიდში ხართ — გადააჭარბეთ!", "You're outbid — place a higher bid!", "Вашу ставку перебили — перебейте!", "Ձեր հայտը գերազանցվեց — բարձրացրեք!")}</span></>}
                                        {myStatus === "idle" && <span>{t("ჯერ არ დაგიბიდავთ", "You haven't bid yet", "Вы ещё не делали ставку", "Դեռ հայտ չեք դրել")}</span>}
                                    </div>
                                )}
                                {isLive ? (
                                    <div className={`auction-ring ${urgent ? "is-urgent" : ""}`}>
                                        <svg viewBox="0 0 120 120" className="auction-ring-svg">
                                            <circle className="auction-ring-track" cx="60" cy="60" r={RING_R} />
                                            <circle className="auction-ring-fill" cx="60" cy="60" r={RING_R}
                                                strokeDasharray={RING_C} strokeDashoffset={RING_C * (1 - elapsedFrac)} />
                                        </svg>
                                        <div className="auction-ring-num">{fmtRemaining(remaining)}</div>
                                    </div>
                                ) : (
                                    <div className="auction-timer is-static">
                                        {status === "open" ? t("წინასწარი ბიდი", "Pre-bid", "Предварительная ставка", "Նախնական հայտ") : DONE.includes(status) ? t("დასრულდა", "Ended", "Завершено", "Ավարտ") : "—"}
                                    </div>
                                )}
                                <div className="auction-live-carrow">
                                    <h2 className="auction-live-car">{currentLot ? carTitle(currentLot) : "..."}</h2>
                                    {currentLot && <WatchButton compact watched={watch.has(currentLot.car_id)} onToggle={() => watch.toggle(currentLot.car_id)} />}
                                </div>
                                {currentLot && (
                                    <p className="auction-muted auction-live-specs">
                                        {[currentLot.year, currentLot.millage ? formatMileage(currentLot.millage) : null, translateSpec("fuel", currentLot.fuel, lang), translateSpec("transmission", currentLot.transmission, lang)].filter(Boolean).join(" · ")}
                                    </p>
                                )}
                                {currentLot && <CarStatusBadges car={currentLot} lang={lang} />}
                                <div className="auction-bigprice">
                                    <span className="auction-muted">{t("მიმდინარე ფასი", "Current price", "Текущая цена", "Ընթացիկ գին")}</span>
                                    <strong>{price != null ? formatPrice(price, "GEL") : "—"}</strong>
                                    {price != null && usdRate && formatUsdFromGel(price, usdRate) && (
                                        <span className="auction-usd-block">≈ {formatUsdFromGel(price, usdRate)}</span>
                                    )}
                                </div>
                                <div className="auction-badges">
                                    {!myStatus && (leadingMe
                                        ? <span className="auction-lead is-me">{t("თქვენ ლიდერობთ", "You're leading", "Вы лидируете", "Դուք առաջատար եք")}</span>
                                        : (live && live.leading ? <span className="auction-lead">{t("ლიდერობს სხვა", "Someone else is leading", "Лидирует другой", "Առաջատարն ուրիշ է")}</span> : <span className="auction-lead is-none">{t("ბიდი არ არის", "No bids", "Ставок нет", "Հայտեր չկան")}</span>))}
                                    {currentLot && currentLot.has_reserve && (
                                        <span className={`auction-reserve ${live && live.reserve_met ? "is-met" : "is-not"}`}>
                                            {live && live.reserve_met ? t("reserve მიღწეულია", "reserve met", "резерв достигнут", "ռեզերվը բավարարված է") : t("reserve არ არის მიღწეული", "reserve not met", "резерв не достигнут", "ռեզերվը չի բավարարվել")}
                                        </span>
                                    )}
                                </div>

                                {(status === "open" || status === "live") ? (
                                    <>
                                        {!me && <p className="auction-muted">{t("ბიდისთვის", "To bid,", "Чтобы сделать ставку,", "Հայտ դնելու համար")} <Link to="/auction/login">{t("შედით", "sign in", "войдите", "մուտք գործեք")}</Link>.</p>}
                                        {me && !canBid && <p className="auction-field-error">{t("ბიდისთვის დაასრულეთ ვერიფიკაცია —", "Finish verification to bid —", "Завершите верификацию для ставок —", "Հայտ դնելու համար ավարտեք վերիֆիկացիան —")} <Link to="/auction/account">{t("ანგარიში", "account", "аккаунт", "հաշիվ")}</Link>.</p>}
                                        <button className="cx-btn cx-btn-primary auction-bid-btn" disabled={busy || !canBid} onClick={() => place("bid:place", minNext)}>
                                            {t("ბიდი", "Bid", "Ставка", "Հայտ")} {formatPrice(minNext, "GEL")}
                                        </button>
                                        <div className="auction-max">
                                            <input className="form-control" inputMode="numeric" placeholder={t("მაქს. ფასი (ავტო-ბიდი)", "Max price (autobid)", "Макс. цена (автоставка)", "Առավելագույն գին (ավտոհայտ)")} value={maxInput} onChange={(e) => setMaxInput(e.target.value)} />
                                            <button className="cx-btn cx-btn-ghost" disabled={busy || !canBid || !maxInput} onClick={() => { place("proxy:set", Number(maxInput)); setMaxInput(""); }}>{t("ავტობიდი", "Autobid", "Автоставка", "Ավտոհայտ")}</button>
                                        </div>
                                        <p className="auction-muted auction-min-hint">{t("მინ. შემდეგი ბიდი:", "Min. next bid:", "Мин. следующая ставка:", "Նվազ. հաջորդ հայտ.")} {formatPrice(minNext, "GEL")}</p>
                                    </>
                                ) : (
                                    <p className="auction-muted auction-live-waitnext">{t("შემდეგი ლოტი იტვირთება...", "Loading the next lot...", "Загружается следующий лот...", "Հաջորդ լოտը բեռնվում է...")}</p>
                                )}

                                {/* Live bid feed: who is bidding + their country (by IP). */}
                                <div className="auction-feed">
                                    <h4 className="auction-feed-title">{t("ბიდების ისტორია", "Bid history", "История ставок", "Հայտերի պատմություն")}</h4>
                                    {feed.length === 0 ? (
                                        <p className="auction-feed-empty">{t("ჯერ ბიდი არ არის", "No bids yet", "Ставок пока нет", "Դեռ հայտ չկա")}</p>
                                    ) : (
                                        <ul className="auction-feed-list">
                                            {feed.map((f) => {
                                                const flag = flagEmoji(f.country_code);
                                                const cname = countryName(f.country_code, lang);
                                                return (
                                                    <li key={f.key} className="auction-feed-row">
                                                        <span className="auction-feed-who">
                                                            {flag && <span className="auction-feed-flag" title={cname} aria-hidden="true">{flag}</span>}
                                                            <span className="auction-feed-name">{f.name || t("ბიდერი", "Bidder", "Участник", "Մասնակից")}</span>
                                                            {cname && <span className="auction-feed-country">{cname}</span>}
                                                        </span>
                                                        <span className="auction-feed-amt">{formatPrice(f.amount, "GEL")}</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Queue */}
                <aside className="auction-live-queue">
                    <h3>{t("რიგი", "Queue", "Очередь", "Հերթ")} ({totalLots})</h3>
                    <div className="auction-live-queue-list">
                        {sortedQueue.map((lot) => {
                            const watched = watch.has(lot.car_id);
                            return (
                                <div key={lot.id} ref={lot.id === currentLotId ? currentRowRef : null} className={`auction-queue-row ${lot.id === currentLotId ? "is-current" : ""} ${DONE.includes(lot.status) ? "is-done" : ""} ${watched ? "is-watched" : ""}`}>
                                    <span className="auction-queue-pos">{lot.position}</span>
                                    <div className="auction-queue-thumb" style={toImageArray(lot.images)[0] ? { backgroundImage: `url('${toImageArray(lot.images)[0]}')` } : {}}>
                                        {watched && <span className="auction-queue-star" title={t("რჩეულებში", "In watchlist", "В избранном", "Ցանկում")}><StarFill /></span>}
                                    </div>
                                    <div className="auction-queue-info">
                                        <strong>{carTitle(lot)}</strong>
                                        <span className="auction-muted">{formatPrice(lot.current_price || lot.start_price, "GEL")}</span>
                                        <CarStatusBadges car={lot} lang={lang} compact />
                                    </div>
                                    {chipFor(lot)}
                                </div>
                            );
                        })}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default AuctionLive;
